// Shared Web Audio primitives.
//
// Browsers cap the number of simultaneous AudioContext instances and you can
// only call `createMediaElementSource` ONCE per HTMLMediaElement, so both the
// visualizer and the tone-layer engine consume the same shared context and
// the same per-element wrapper cache.

let SHARED_CTX = null;

export function getAudioContext() {
  if (SHARED_CTX && SHARED_CTX.state !== "closed") return SHARED_CTX;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    SHARED_CTX = new Ctor();
    return SHARED_CTX;
  } catch (_e) {
    return null;
  }
}

export function resumeAudioContextOnGesture() {
  const ctx = SHARED_CTX;
  if (ctx && ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
}

const ELEM_WRAPPED = new WeakMap(); // HTMLMediaElement -> { ctx, source, analyser }

/** Returns (and caches) the {ctx, source, analyser} wrapper for an audio/video element.
 *  - source.connect(analyser) and analyser.connect(ctx.destination) so audio still plays.
 *  - If the element is CORS-tainted or wrapping fails, returns null and the caller
 *    should fall back to a non-Web-Audio path (visualizer falls back to fake EQ;
 *    tone layer simply skips and keeps Original Mix audible).
 */
export function getMediaWrapper(el) {
  if (!el) return null;
  if (ELEM_WRAPPED.has(el)) return ELEM_WRAPPED.get(el);
  const ctx = getAudioContext();
  if (!ctx) return null;
  try {
    const source = ctx.createMediaElementSource(el);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.78;
    source.connect(analyser);
    analyser.connect(ctx.destination);
    const wrap = { ctx, source, analyser };
    ELEM_WRAPPED.set(el, wrap);
    return wrap;
  } catch (_e) {
    return null;
  }
}

// ---------------- Tone layer engine ----------------
// Singleton OscillatorNode + GainNode mixed into the same destination as the
// track audio. We re-create the oscillator on each enable because oscillators
// are one-shot in Web Audio (cannot be restarted after stop()).

let TONE = null; // { oscillator, gain, freq, started }

function ensureToneNodes(ctx) {
  if (!ctx) return null;
  if (TONE && TONE.oscillator && TONE.gain) return TONE;
  const gain = ctx.createGain();
  gain.gain.value = 0; // start silent — fade in via setTargetAtTime
  gain.connect(ctx.destination);
  const oscillator = ctx.createOscillator();
  oscillator.type = "sine";
  oscillator.connect(gain);
  TONE = { oscillator, gain, freq: 0, started: false };
  return TONE;
}

/** Start (or update) a sine tone layer. `volume` is 0..1. `freqHz` 20..2000. */
export function startToneLayer(freqHz, volume) {
  const ctx = getAudioContext();
  if (!ctx) return false;
  const t = ensureToneNodes(ctx);
  if (!t) return false;
  try {
    if (!t.started) {
      t.oscillator.start();
      t.started = true;
    }
    t.oscillator.frequency.setValueAtTime(freqHz, ctx.currentTime);
    t.freq = freqHz;
    // Smooth fade to avoid clicks
    const target = Math.max(0, Math.min(0.2, Number(volume) || 0));
    t.gain.gain.setTargetAtTime(target, ctx.currentTime, 0.08);
    return true;
  } catch (_e) {
    return false;
  }
}

/** Update only the tone layer volume without changing frequency. */
export function setToneLayerVolume(volume) {
  if (!TONE || !TONE.gain) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const target = Math.max(0, Math.min(0.2, Number(volume) || 0));
  try {
    TONE.gain.gain.setTargetAtTime(target, ctx.currentTime, 0.08);
  } catch (_e) {}
}

/** Fade out + fully stop the tone layer (one-shot — must be re-started). */
export function stopToneLayer() {
  if (!TONE) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    TONE.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.08);
    const node = TONE.oscillator;
    setTimeout(() => {
      try { node.stop(); } catch (_e) {}
      try { node.disconnect(); } catch (_e) {}
      try { TONE && TONE.gain && TONE.gain.disconnect(); } catch (_e) {}
      TONE = null;
    }, 250);
  } catch (_e) {
    TONE = null;
  }
}

export function isToneLayerRunning() {
  return !!(TONE && TONE.started);
}
